import { useState, useEffect, useCallback } from 'react';
import { financingsApi } from '../../../../services/api/financings';
import { AggregatedRepaymentPlanResponse } from '../../../../types';

interface UseRepaymentPlanProps {
  projectId: string;
  /** Wird inkrementiert, um einen Reload auszulösen (z.B. nach Darlehens-Mutation) */
  reloadTrigger: number;
}

interface UseRepaymentPlanReturn {
  repaymentPlanResponse: AggregatedRepaymentPlanResponse | null;
  /** True beim initialen Laden (Section zeigt Spinner) */
  isLoading: boolean;
  /** True beim Nachladen nach Mutation (dezentes Overlay, Expand-State bleibt erhalten) */
  isReloading: boolean;
  error: string | null;
}

/**
 * Eigenständiger Hook für den aggregierten Zins- und Tilgungsplan.
 *
 * Die Berechnung findet ausschließlich im Backend statt (zu komplex für Frontend-Duplizierung).
 * Der Hook verwaltet seinen eigenen Loading-State, sodass die RepaymentSection
 * unabhängig von den anderen Sections laden und nachladen kann.
 *
 * Steuerung über reloadTrigger:
 * - Startwert 0 → initialer Load beim Mount
 * - Inkrementierung → Reload nach Darlehens-Mutation (z.B. Create/Update/Delete)
 */
export const useRepaymentPlan = ({ projectId, reloadTrigger }: UseRepaymentPlanProps): UseRepaymentPlanReturn => {
  const [repaymentPlanResponse, setRepaymentPlanResponse] =
    useState<AggregatedRepaymentPlanResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReloading, setIsReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRepaymentPlan = useCallback(async (isInitial: boolean) => {
    try {
      if (isInitial) {
        setIsLoading(true);
      } else {
        setIsReloading(true);
      }
      setError(null);

      const data = await financingsApi.getAggregatedRepaymentPlan(projectId);
      setRepaymentPlanResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden des Tilgungsplans');
    } finally {
      setIsLoading(false);
      setIsReloading(false);
    }
  }, [projectId]);

  // Initiales Laden
  useEffect(() => {
    loadRepaymentPlan(true);
  }, [loadRepaymentPlan]);

  // Reload bei Trigger-Änderung (nicht beim initialen Mount mit Wert 0)
  useEffect(() => {
    if (reloadTrigger > 0) {
      loadRepaymentPlan(false);
    }
  }, [reloadTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    repaymentPlanResponse,
    isLoading,
    isReloading,
    error,
  };
};
